import { AnimatePresence } from 'motion/react';
import { useEffect, useState } from 'react';

import { useLanguage } from '../lib/i18n/LanguageContext';
import { useSession } from '../lib/SessionContext';
import { Modal } from './Modal';

const SEEN_KEY = 'llmr-org-chooser-seen';

/**
 * Called from the sign-out handler so a DIFFERENT account signing in on the
 * same tab sees the chooser too, rather than inheriting the previous
 * account's "already seen" flag — sessionStorage otherwise survives a
 * sign-out/sign-in that never closes the tab.
 */
export function markOrgChooserUnseen() {
  sessionStorage.removeItem(SEEN_KEY);
}

/**
 * Forces a multi-org account to see and acknowledge which org they're
 * landing in, once per browser session — rather than silently defaulting
 * (owner > admin > member, unchanged from before LLMR-54) and leaving them
 * to notice the topbar's `OrgSwitcher` on their own, if ever.
 *
 * `sessionStorage`, not component state: the whole app reloads on every
 * `switchOrg` (see SessionContext), including picking an org from THIS
 * modal — a plain "have I shown this yet" boolean would reset on that
 * reload and show itself again immediately after being answered.
 *
 * Mounted once at the signed-in shell level (Layout), so it applies
 * regardless of which page a session lands on first.
 */
export function OrgChooserModal() {
  const { ready, me, switchOrg } = useSession();
  const { dict } = useLanguage();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!ready || !me) return;
    if (me.organizations.length > 1 && sessionStorage.getItem(SEEN_KEY) !== '1') {
      setOpen(true);
    }
  }, [ready, me]);

  function choose(orgId: string) {
    sessionStorage.setItem(SEEN_KEY, '1');
    if (orgId === me?.active_org_id) {
      // Already the org they'd land on — nothing to switch, just close.
      setOpen(false);
      return;
    }
    void switchOrg(orgId); // reloads the page on success; modal unmounts with it
  }

  return (
    <AnimatePresence>
      {open && me && (
        <Modal title={dict.orgChooser.title} dismissible={false}>
          <p className="note">{dict.orgChooser.body}</p>
          <div className="org-chooser-list">
            {me.organizations.map((o) => (
              <button key={o.org_id} type="button" className="org-chooser-item" onClick={() => choose(o.org_id)}>
                <span className="org-chooser-item-text">
                  <span className="org-chooser-item-name">{o.name}</span>
                  <span className="org-chooser-item-role">{dict.team.role[o.role]}</span>
                </span>
                {o.org_id === me.active_org_id && (
                  <span className="org-chooser-item-badge">{dict.orgChooser.defaultBadge}</span>
                )}
              </button>
            ))}
          </div>
        </Modal>
      )}
    </AnimatePresence>
  );
}
